import { useState, useMemo, useEffect } from "react";
import { Search, Eye, BookOpen, History } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { DropdownCategoriaPesquisavel } from "@/modules/biblioteca/components/DropdownCategoriaPesquisavel";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { Paginacao } from "@/shared/components/Paginacao";
import { useAuth } from "@/core/hooks/useAuth";
import { useCatalogo } from "@/modules/biblioteca/hooks/useLivros";
import { useMeusEmprestimos, useSolicitarEmprestimo, useMarcarRejeicaoVisualizada } from "@/modules/biblioteca/hooks/useEmprestimos";
import { useCategorias } from "@/modules/biblioteca/hooks/useCategorias";
import { STATUS_LABEL } from "@/modules/biblioteca/services/emprestimosService";
import { CORES_STATUS, formatarData } from "@/modules/biblioteca/utils/emprestimos";

const ITENS_POR_PAGINA = 10;

type FiltroDisponibilidade = "todos" | "disponiveis" | "indisponiveis";

interface DashboardUsuarioProps {
  /**
   * Usado quando o admin acessa "Visualizar como usuário" (Fase 8). Mostra
   * a mesma interface do usuário comum, mas com um banner informativo no
   * topo — as ações aqui são REAIS e usam a própria conta do administrador
   * (ele pode solicitar e depois aprovar/rejeitar/devolver normalmente,
   * inclusive a própria solicitação, por decisão de produto).
   */
  modoPreview?: boolean;
}

export function DashboardUsuario({ modoPreview = false }: DashboardUsuarioProps) {
  const { pessoa, modulosVisiveis } = useAuth();
  const location = useLocation();
  const abaInicial = (location.state as { abaInicial?: "catalogo" | "meus" } | null)?.abaInicial;
  const [aba, setAba] = useState<"catalogo" | "meus">(abaInicial ?? "catalogo");

  // Reage à navegação vinda de outra tela (ex.: "Meu perfil" → "Ver meus
  // empréstimos") quando já estamos aqui: cada navegação troca location.key.
  useEffect(() => {
    if (abaInicial) setAba(abaInicial);
  }, [abaInicial, location.key]);
  const [subabaMeusEmprestimos, setSubabaMeusEmprestimos] = useState<"ativos" | "historico">("ativos");
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>("todas");
  const [disponibilidadeFiltro, setDisponibilidadeFiltro] = useState<FiltroDisponibilidade>("todos");
  const [pesquisa, setPesquisa] = useState("");

  const { data: livros, isLoading: carregandoLivros } = useCatalogo();
  const { data: categorias } = useCategorias();
  const { data: meusEmprestimos, isLoading: carregandoEmprestimos } = useMeusEmprestimos(pessoa?.id);
  const solicitar = useSolicitarEmprestimo();

  const [mensagem, setMensagem] = useState<{ tipo: "sucesso" | "erro"; texto: string } | null>(null);
  const [livroParaConfirmar, setLivroParaConfirmar] = useState<{ id: string; titulo: string } | null>(null);

  // Subabas de Meus Empréstimos: "Ativos" = livro realmente em posse do
  // usuário (Em aberto/Atrasado). Pendente vira um banner informativo à
  // parte (amarelo), e Rejeitado vira outro banner à parte (cinza,
  // dispensável) — nenhum dos dois aparece nas subabas em si.
  const emprestimosPendentes = useMemo(
    () => (meusEmprestimos ?? []).filter((e) => e.status === "PENDENTE"),
    [meusEmprestimos],
  );
  const emprestimosRejeitadosNaoVistos = useMemo(
    () => (meusEmprestimos ?? []).filter((e) => e.status === "REJEITADO" && !e.visualizado_pelo_usuario),
    [meusEmprestimos],
  );
  const emprestimosAtivos = useMemo(
    () => (meusEmprestimos ?? []).filter((e) => ["EM_ABERTO", "ATRASADO"].includes(e.status)),
    [meusEmprestimos],
  );
  const emprestimosHistorico = useMemo(
    () => (meusEmprestimos ?? []).filter((e) => e.status === "DEVOLVIDO"),
    [meusEmprestimos],
  );

  const marcarRejeicaoVisualizada = useMarcarRejeicaoVisualizada();

  // Paginação padrão do sistema (10 por página), independente para cada subaba.
  const [paginaAtivos, setPaginaAtivos] = useState(1);
  const [paginaHistorico, setPaginaHistorico] = useState(1);

  const totalPaginasAtivos = Math.max(1, Math.ceil(emprestimosAtivos.length / ITENS_POR_PAGINA));
  const ativosPaginados = useMemo(() => {
    const inicio = (paginaAtivos - 1) * ITENS_POR_PAGINA;
    return emprestimosAtivos.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [emprestimosAtivos, paginaAtivos]);

  const totalPaginasHistorico = Math.max(1, Math.ceil(emprestimosHistorico.length / ITENS_POR_PAGINA));
  const historicoPaginado = useMemo(() => {
    const inicio = (paginaHistorico - 1) * ITENS_POR_PAGINA;
    return emprestimosHistorico.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [emprestimosHistorico, paginaHistorico]);

  const DOT_STATUS: Record<string, string> = {
    PENDENTE: "bg-amber-400",
    EM_ABERTO: "bg-blue-500",
    ATRASADO: "bg-red-500",
    DEVOLVIDO: "bg-emerald-500",
    REJEITADO: "bg-neutral-300",
  };

  // IDs de livros com empréstimo ativo do usuário (sem query extra ao banco)
  const livrosComEmprestimoAtivo = useMemo(() => {
    const statusAtivos = new Set(["PENDENTE", "EM_ABERTO", "ATRASADO"]);
    return new Set(
      (meusEmprestimos ?? [])
        .filter((e) => statusAtivos.has(e.status))
        .map((e) => e.livro_id),
    );
  }, [meusEmprestimos]);

  // Pesquisa + filtros aplicados juntos
  const livrosFiltrados = useMemo(() => {
    // Livro Inativo (status gravado como "Indisponivel") está fora de circulação: não aparece
    // para o usuário comum. "Indisponível" aqui passa a significar só "sem exemplar livre".
    let lista = (livros ?? []).filter((l) => l.status === "Disponivel");

    // Filtro de disponibilidade
    if (disponibilidadeFiltro === "disponiveis") {
      lista = lista.filter((l) => l.quantidade_disponivel > 0);
    } else if (disponibilidadeFiltro === "indisponiveis") {
      lista = lista.filter((l) => l.quantidade_disponivel === 0);
    }

    // Filtro de categoria
    if (categoriaFiltro !== "todas") {
      lista = lista.filter((l) => l.categorias.some((c) => c.id === categoriaFiltro));
    }

    // Pesquisa por título ou autor (dinâmica, sem necessidade de confirmar)
    const termo = pesquisa.trim().toLowerCase();
    if (termo) {
      lista = lista.filter(
        (l) =>
          l.nome_livro.toLowerCase().includes(termo) ||
          (l.autor ?? "").toLowerCase().includes(termo),
      );
    }

    return lista;
  }, [livros, disponibilidadeFiltro, categoriaFiltro, pesquisa]);

  const [paginaAtual, setPaginaAtual] = useState(1);

  // Reseta para a página 1 sempre que filtro/pesquisa mudar, evitando ficar
  // "preso" em uma página vazia após reduzir os resultados.
  const chaveFiltros = `${disponibilidadeFiltro}-${categoriaFiltro}-${pesquisa}`;
  const [chaveFiltrosAnterior, setChaveFiltrosAnterior] = useState(chaveFiltros);
  if (chaveFiltros !== chaveFiltrosAnterior) {
    setChaveFiltrosAnterior(chaveFiltros);
    setPaginaAtual(1);
  }

  const totalPaginas = Math.max(1, Math.ceil(livrosFiltrados.length / ITENS_POR_PAGINA));
  const livrosPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return livrosFiltrados.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [livrosFiltrados, paginaAtual]);

  function estaDisponivel(livro: (typeof livrosFiltrados)[number]): boolean {
    return livro.status === "Disponivel" && livro.quantidade_disponivel > 0;
  }

  async function handleSolicitar(livroId: string) {
    setMensagem(null);
    if (livrosComEmprestimoAtivo.has(livroId)) {
      setMensagem({ tipo: "erro", texto: "Você já possui um empréstimo em andamento para este livro." });
      setLivroParaConfirmar(null);
      return;
    }
    try {
      const resultado = await solicitar.mutateAsync(livroId);
      const texto =
        resultado.outrasSolicitacoesPendentes > 0
          ? `Solicitação enviada! Existem outras ${resultado.outrasSolicitacoesPendentes === 1 ? "solicitação pendente" : "solicitações pendentes"} para este livro. A sua será analisada por quem gerencia a Biblioteca, na ordem de chegada.`
          : "Solicitação enviada! Aguarde a aprovação de quem gerencia a Biblioteca.";
      setMensagem({ tipo: "sucesso", texto });
    } catch (erro) {
      setMensagem({
        tipo: "erro",
        texto: erro instanceof Error ? erro.message : "Não foi possível solicitar o empréstimo.",
      });
    } finally {
      setLivroParaConfirmar(null);
    }
  }

  // G6 (DA-002 — visibilidade ≠ permissão): o conteúdo da Biblioteca só
  // aparece se o módulo estiver ativo e visível para esta pessoa
  // (igreja_modulos, via modulos_visiveis — migration 0023). Admin da
  // plataforma e gestores sempre veem (a função do banco garante isso).
  const veBiblioteca = modulosVisiveis.includes("biblioteca");

  if (!veBiblioteca) {
    return (
      <main className="mx-auto w-full max-w-4xl px-4 pb-12 pt-6 sm:px-6 sm:py-10">
          <div className="rounded-2xl border border-border bg-surface p-8 text-center">
            <h1 className="text-xl font-bold text-brand-verde">Nenhum elo disponível</h1>
            <p className="mt-2 text-sm text-muted">
              No momento não há elos liberados para o seu perfil. Se você acha
              que isso é um engano, fale com a administração da igreja.
            </p>
          </div>
      </main>
    );
  }

  return (
    <>
      {modoPreview && (
        <div className="flex items-center justify-center gap-2 bg-amber-400 px-4 py-2 text-center text-xs font-semibold text-amber-950 sm:text-sm">
          <Eye size={14} />
          Você está usando a visão do membro · as ações aqui usam sua própria conta de administrador
          <Link to="/admin" className="ml-2 underline">
            Voltar ao painel
          </Link>
        </div>
      )}
      <main className="mx-auto w-full max-w-4xl px-4 pb-16 pt-6 sm:px-6 sm:pt-10">

        <header className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-brand-verde sm:text-3xl">
              Meu painel
            </h1>
            <p className="mt-1 text-sm text-muted">
              Olá, {pessoa?.nome_completo?.split(" ")[0]}! Encontre livros e acompanhe seus empréstimos.
            </p>
          </div>
        </header>

        {/* Menu principal — barra segmentada com fundo próprio, visualmente
            distinta dos filtros abaixo, para deixar claro que é navegação
            de topo e não um filtro. */}
        <div className="flex gap-1 rounded-2xl bg-brand-verde-hover p-1">
          <button
            onClick={() => setAba("catalogo")}
            className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl py-2.5 text-sm font-bold transition-all ${
              aba === "catalogo" ? "bg-surface text-brand-verde" : "text-white/75"
            }`}
          >
            <BookOpen size={15} />
            Catálogo
          </button>
          <button
            onClick={() => setAba("meus")}
            className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl py-2.5 text-sm font-bold transition-all ${
              aba === "meus" ? "bg-surface text-brand-verde" : "text-white/75"
            }`}
          >
            <History size={15} />
            Meus empréstimos
            {meusEmprestimos && meusEmprestimos.filter((e) => ["EM_ABERTO", "ATRASADO"].includes(e.status)).length > 0 && (
              <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-black text-amber-950">
                {meusEmprestimos.filter((e) => ["EM_ABERTO", "ATRASADO"].includes(e.status)).length}
              </span>
            )}
          </button>
        </div>

        {/* Mensagem de feedback */}
        {mensagem && (
          <div
            role="alert"
            className={`mt-4 rounded-xl border p-3 text-sm ${
              mensagem.tipo === "sucesso"
                ? "border-success/30 bg-success/10 text-success"
                : "border-danger/40 bg-danger/10 text-danger"
            }`}
          >
            {mensagem.texto}
          </div>
        )}

        {/* ── CATÁLOGO ── */}
        {aba === "catalogo" && (
          <div className="mt-6">

            {/* Painel de busca e filtros — visualmente agrupado (cartão
                branco com legenda) para deixar claro que controla a lista
                de livros abaixo, distinto do menu principal acima. */}
            <div className="rounded-2xl bg-surface p-3">
              <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-wider text-muted">
                Buscar e filtrar
              </p>

              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="search"
                  placeholder="Pesquisar por título ou autor..."
                  value={pesquisa}
                  onChange={(e) => setPesquisa(e.target.value)}
                  className="w-full rounded-xl border border-border bg-surface-2 py-3 pl-11 pr-4 text-sm text-text placeholder:text-muted focus:border-brand-verde focus:outline-none focus:ring-1 focus:ring-brand-verde"
                />
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {(
                  [
                    { valor: "todos", label: "Todos" },
                    { valor: "disponiveis", label: "Disponíveis" },
                    { valor: "indisponiveis", label: "Indisponíveis" },
                  ] as { valor: FiltroDisponibilidade; label: string }[]
                ).map((f) => (
                  <button
                    key={f.valor}
                    onClick={() => setDisponibilidadeFiltro(f.valor)}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${
                      disponibilidadeFiltro === f.valor
                        ? "bg-brand-verde text-white"
                        : "bg-surface-2 text-muted"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}

                {/* Filtro de categoria (com pesquisa interna) */}
                {categorias && categorias.length > 0 && (
                  <DropdownCategoriaPesquisavel
                    categorias={categorias}
                    valorSelecionado={categoriaFiltro}
                    onSelecionar={setCategoriaFiltro}
                  />
                )}
              </div>
            </div>

            {/* Legenda separando os controles acima do conteúdo abaixo */}
            {!carregandoLivros && (
              <p className="mt-4 px-1 text-xs text-muted">
                {livrosFiltrados.length} {livrosFiltrados.length === 1 ? "livro encontrado" : "livros encontrados"}
              </p>
            )}

            {/* Grid de livros */}
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {carregandoLivros ? (
                <p className="text-sm text-muted">Carregando livros...</p>
              ) : !livrosFiltrados.length ? (
                <div className="col-span-full rounded-2xl border border-dashed border-border bg-surface py-10 text-center">
                  <p className="text-sm text-muted">Nenhum livro encontrado.</p>
                  {pesquisa && (
                    <button
                      onClick={() => setPesquisa("")}
                      className="mt-2 text-xs font-bold text-brand-verde hover:underline"
                    >
                      Limpar pesquisa
                    </button>
                  )}
                </div>
              ) : (
                livrosPaginados.map((livro) => {
                  const disponivel = estaDisponivel(livro);
                  const jasolicitado = livrosComEmprestimoAtivo.has(livro.id);

                  return (
                    <div
                      key={livro.id}
                      className={`flex flex-col rounded-2xl border bg-surface p-5 transition-all ${
                        disponivel
                          ? "border-border hover:border-brand-verde/30 hover:shadow-lg hover:shadow-brand-verde/5"
                          : "border-border opacity-70"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="break-words font-bold text-text">{livro.nome_livro}</h3>
                          {/* Badge de disponibilidade */}
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${
                              disponivel
                                ? "bg-success/10 text-success"
                                : "bg-surface-2 text-muted"
                            }`}
                          >
                            {disponivel ? `${livro.quantidade_disponivel} disp.` : "Indisponível"}
                          </span>
                        </div>

                        {livro.autor && (
                          <p className="mt-0.5 text-sm text-muted">por {livro.autor}</p>
                        )}

                        {!!livro.categorias.length && (
                          <span className="mt-2 flex flex-wrap gap-1">
                            {livro.categorias.map((c) => (
                              <span
                                key={c.id}
                                className="inline-block rounded-lg bg-surface-2 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-verde"
                              >
                                {c.nome}
                              </span>
                            ))}
                          </span>
                        )}

                        {/* Descrição do livro */}
                        {livro.descricao ? (
                          <p className="mt-3 text-xs leading-relaxed text-muted">
                            {livro.descricao}
                          </p>
                        ) : (
                          <p className="mt-3 text-xs italic text-muted">
                            Este livro ainda não possui descrição.
                          </p>
                        )}
                      </div>

                      {/* Botão de solicitar */}
                      <button
                        onClick={() => setLivroParaConfirmar({ id: livro.id, titulo: livro.nome_livro })}
                        disabled={!disponivel || jasolicitado || solicitar.isPending}
                        className={`mt-5 w-full rounded-xl py-3 text-sm font-black transition-all active:scale-95 disabled:cursor-not-allowed ${
                          jasolicitado
                            ? "bg-surface-2 text-muted"
                            : !disponivel
                              ? "bg-surface-2 text-muted"
                              : "bg-brand-verde text-white hover:bg-brand-verde/90 disabled:opacity-60"
                        }`}
                      >
                        {jasolicitado
                          ? "Já solicitado"
                          : !disponivel
                            ? "Indisponível"
                            : solicitar.isPending
                              ? "Solicitando..."
                              : "Solicitar empréstimo"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <Paginacao
              paginaAtual={paginaAtual}
              totalPaginas={totalPaginas}
              onMudarPagina={setPaginaAtual}
            />
          </div>
        )}

        {/* ── MEUS EMPRÉSTIMOS ── */}
        {aba === "meus" && (
          <div className="mt-6">
            <>
              {/* Banner informativo — livros aguardando aprovação do admin.
                  Não é uma aba/lista própria, só um aviso (mesma ideia do
                  banner de "cadastros pendentes" que o admin já vê). */}
              {!modoPreview && emprestimosPendentes.length > 0 && (
                <div className="mb-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
                  Você {emprestimosPendentes.length === 1 ? "tem 1 livro" : `tem ${emprestimosPendentes.length} livros`}{" "}
                  aguardando aprovação de quem gerencia a Biblioteca.
                </div>
              )}

              {/* Banner de empréstimos recusados — cinza (mesma cor do
                  status Rejeitado), com botão para dispensar cada aviso.
                  Sem isso, o usuário nunca saberia que sua solicitação foi
                  negada, já que Rejeitado não aparece nas subabas. */}
              {!modoPreview && emprestimosRejeitadosNaoVistos.length > 0 && (
                <div className="mb-3 flex flex-col gap-2">
                  {emprestimosRejeitadosNaoVistos.map((emp) => (
                    <div
                      key={emp.id}
                      className="flex flex-col gap-2 rounded-xl border border-border bg-surface-2 px-4 py-3 text-sm text-text sm:flex-row sm:items-center sm:justify-between"
                    >
                      <p>
                        Sua solicitação de <span className="font-bold">"{emp.livro_titulo}"</span> foi recusada
                        por quem gerencia a Biblioteca. Caso tenha dúvidas, procure a administração da igreja.
                      </p>
                      <button
                        onClick={() => marcarRejeicaoVisualizada.mutate(emp.id)}
                        disabled={marcarRejeicaoVisualizada.isPending}
                        className="shrink-0 self-start rounded-lg bg-surface-2 px-3 py-1.5 text-xs font-bold text-white hover:bg-surface-2 disabled:opacity-60 sm:self-auto"
                      >
                        Entendi
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Subabas: Ativos (Em aberto/Atrasado) / Histórico (Devolvido) */}
              <div className="flex gap-2">
                <button
                  onClick={() => setSubabaMeusEmprestimos("ativos")}
                  className={`flex-1 rounded-xl py-2.5 text-sm font-bold transition-all ${
                    subabaMeusEmprestimos === "ativos"
                      ? "bg-success/10 text-success ring-1 ring-success/40"
                      : "border border-border bg-surface text-muted"
                  }`}
                  >
                    Em andamento {emprestimosAtivos.length > 0 && `(${emprestimosAtivos.length})`}
                  </button>
                  <button
                    onClick={() => setSubabaMeusEmprestimos("historico")}
                    className={`flex-1 rounded-xl py-2.5 text-sm font-bold transition-all ${
                      subabaMeusEmprestimos === "historico"
                        ? "bg-success/10 text-success ring-1 ring-success/40"
                        : "border border-border bg-surface text-muted"
                    }`}
                  >
                    Histórico
                  </button>
                </div>

                <div className="mt-3">
                  {carregandoEmprestimos ? (
                    <p className="text-sm text-muted">Carregando seus empréstimos...</p>
                  ) : subabaMeusEmprestimos === "ativos" ? (
                    !emprestimosAtivos.length ? (
                      <div className="rounded-2xl border border-dashed border-border bg-surface py-12 text-center">
                        <p className="text-sm text-muted">Nenhum empréstimo em andamento no momento.</p>
                        <button onClick={() => setAba("catalogo")} className="mt-3 text-sm font-bold text-brand-verde hover:underline">
                          Explorar o catálogo
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                          <ul>
                            {ativosPaginados.map((emp, idx) => (
                              <li key={emp.id} className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}>
                                <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_STATUS[emp.status]}`} />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-semibold text-text">{emp.livro_titulo}</p>
                                  <p className="mt-0.5 text-xs text-muted">
                                    {formatarData(emp.data_solicitacao)}
                                    {emp.data_devolucao_prevista && <span> · prazo {formatarData(emp.data_devolucao_prevista)}</span>}
                                  </p>
                                </div>
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${CORES_STATUS[emp.status]}`}>
                                  {STATUS_LABEL[emp.status]}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <Paginacao
                          paginaAtual={paginaAtivos}
                          totalPaginas={totalPaginasAtivos}
                          onMudarPagina={setPaginaAtivos}
                        />
                      </>
                    )
                  ) : !emprestimosHistorico.length ? (
                    <div className="rounded-2xl border border-dashed border-border bg-surface py-12 text-center">
                      <p className="text-sm text-muted">Nenhum empréstimo no histórico ainda.</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                        <ul>
                          {historicoPaginado.map((emp, idx) => (
                            <li key={emp.id} className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}>
                              <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_STATUS[emp.status]}`} />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-text">{emp.livro_titulo}</p>
                                <p className="mt-0.5 text-xs text-muted">
                                  {emp.status === "DEVOLVIDO" && emp.data_devolucao_real
                                    ? `Devolvido em ${formatarData(emp.data_devolucao_real)}`
                                    : `Solicitado em ${formatarData(emp.data_solicitacao)}`}
                                </p>
                              </div>
                              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${CORES_STATUS[emp.status]}`}>
                                {STATUS_LABEL[emp.status]}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Paginacao
                        paginaAtual={paginaHistorico}
                        totalPaginas={totalPaginasHistorico}
                        onMudarPagina={setPaginaHistorico}
                      />
                    </>
                  )}
                </div>
              </>
          </div>
        )}
      </main>

      <ModalConfirmacao
        aberto={!!livroParaConfirmar}
        titulo="Solicitar empréstimo"
        mensagem={`Confirma a solicitação de empréstimo de "${livroParaConfirmar?.titulo}"?`}
        textoBotaoConfirmar="Solicitar"
        variante="padrao"
        carregando={solicitar.isPending}
        onConfirmar={() => livroParaConfirmar && handleSolicitar(livroParaConfirmar.id)}
        onCancelar={() => setLivroParaConfirmar(null)}
      />
    </>
  );
}
